import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { HelperProfile, WorkExperience, HelperDocument } from '../types';
import { apiService } from '../services/api';
import { useDataDictionaries, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictDisplayValue, resolveApiError } from '../utils/i18n';
import { DollarSign, Calendar, Edit, MapPin, Check, GraduationCap, Briefcase, ChefHat, FileText, Download, Sparkles, Globe, Clock, Activity, Home, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { getAccommodationOptions } from '../constants/formOptions';
import { CookingProficiencyTooltip } from '../components/CookingProficiencyTooltip';
import { formatDate as fmtDate, calculateAge, calculateDuration as calcDuration, parseServerDate } from '../utils/date';

const maskDocumentNumber = (value?: string | null): string | null => {
  if (!value) return null;
  const visible = value.slice(0, 4);
  return visible + '*'.repeat(Math.max(4, value.length - visible.length));
};


const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const [helperProfile, setHelperProfile] = useState<HelperProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(user?.role === 'HELPER');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: user?.username || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    whatsappPhone: user?.whatsappPhone || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setEditForm({
        username: user.username || '',
        lastName: user.lastName || '',
        email: user.email || '',
        whatsappPhone: user.whatsappPhone || '',
      });
    }
  }, [user?.username, user?.lastName, user?.email, user?.whatsappPhone]);

  const profileParentCodes = [
    DATA_DICTIONARY_TYPES.NATIONALITY,
    DATA_DICTIONARY_TYPES.MARITAL_STATUS,
    DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
    DATA_DICTIONARY_TYPES.JOB_LOCATION,
    DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
    DATA_DICTIONARY_TYPES.COOKING_SKILLS,
    DATA_DICTIONARY_TYPES.GENDER,
    DATA_DICTIONARY_TYPES.RELIGION,
    DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
    DATA_DICTIONARY_TYPES.HOUSEWORK,
    DATA_DICTIONARY_TYPES.OTHER_SKILLS,
    DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL,
    DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY,
  ];
  
  const { dictionariesByParent } = useDataDictionaries({ parentCodes: profileParentCodes });
  
  const nationalityDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY) || [];
  const maritalStatusDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.MARITAL_STATUS) || [];
  const educationLevelDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL) || [];
  const genderDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.GENDER) || [];
  const religionDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RELIGION) || [];
  const careDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE) || [];
  const houseworkDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK) || [];
  const otherSkillsDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.OTHER_SKILLS) || [];
  const workExpSkillDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL) || [];
  const cookingSkillsDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.COOKING_SKILLS) || [];
  const languageSkillsDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS) || [];
  const proficiencyDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY) || [];

  const getWorkLocation = (loc: string): string => {
    const map: Record<string, string> = {
      HK:    'helperProfileForm.locHongKong',
      SG:    'helperProfileForm.locSingapore',
      TW:    'helperProfileForm.locTaiwan',
      MY:    'helperProfileForm.locMalaysia',
      ME:    'helperProfileForm.locMiddleEast',
      HOME:  'helperProfileForm.locHomeCountry',
      OTHER: 'helperProfileForm.locOther',
    };
    return map[loc] ? t(map[loc], loc) : loc;
  };
  const getCookingLevelLabel = (code: string) => ({
    BEGINNER:     t('cookingLevel.beginner',     '新手'),
    INTERMEDIATE: t('cookingLevel.intermediate', '中階熟手'),
    ADVANCED:     t('cookingLevel.advanced',     '高階專家'),
  }[code] ?? code);

  const getDictValue = (dicts: any[], code: string, fallback?: string) => {
    const dict = dicts.find(d => d.code === code);
    return dict ? getDictDisplayValue(dict) : (fallback || code);
  };

  const getSkillDisplayValue = (skillCode: string): string => {
    let dict = careDicts.find((d: any) => d.code === skillCode);
    if (dict) return getDictDisplayValue(dict);
    dict = houseworkDicts.find((d: any) => d.code === skillCode);
    if (dict) return getDictDisplayValue(dict);
    dict = otherSkillsDicts.find((d: any) => d.code === skillCode);
    if (dict) return getDictDisplayValue(dict);
    dict = workExpSkillDicts.find((d: any) => d.code === skillCode);
    if (dict) return getDictDisplayValue(dict);
    return skillCode;
  };

  useEffect(() => {
    if (user?.role === 'HELPER' && user?.id) {
      loadHelperProfile();
    } else {
      setProfileLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, i18n.language]);

  const loadHelperProfile = async () => {
    if (!user?.id) return;
    try {
      setProfileLoading(true);
      const response = await apiService.helpers.getByUserId(user.id);
      if (response.success && response.data) {
        setHelperProfile(response.data);
      } else {
        setHelperProfile(null);
      }
    } catch (error) {
      console.error('Error loading helper profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // EMPLOYER view
  if (user?.role === 'EMPLOYER') {
    const handleSave = async () => {
      try {
        setSaving(true);
        const response = await apiService.users.updateProfile(editForm);
        if (response.success) {
          showAlert({ type: 'success', message: t('common.save') + ' ' + t('common.success', 'Success') });
          setIsEditing(false);
          await refreshUser();
        }
      } catch (error: any) {
        showAlert({ type: 'error', message: resolveApiError(error?.response?.data?.errorCode, error?.response?.data?.message, t) });
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <Card className="border-border bg-card text-card-foreground shadow-sm">
          <CardHeader className="flex flex-row justify-between items-center border-b border-border pb-4">
            <div>
              <CardTitle className="text-xl">{t('profile.title', 'My Profile')}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{t('profile.subtitle', 'Manage your profile information')}</p>
            </div>
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                {t('common.edit')}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.username')}:</Label>
              {isEditing ? (
                <Input value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
              ) : (
                <span className="text-foreground font-medium">{user.username}</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.surname')}:</Label>
              {isEditing ? (
                <Input value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} placeholder="e.g. Chan" />
              ) : (
                <span className="text-foreground font-medium">{user.lastName || '-'}</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.email')}:</Label>
              {isEditing ? (
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              ) : (
                <span className="text-foreground">{user.email}</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.whatsapp', '電話')}:</Label>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Input value={editForm.whatsappPhone} disabled className="bg-muted" placeholder="+85212345678" />
                  {user.whatsappPhone && (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${user.phoneVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {user.phoneVerified ? t('profile.verified') : t('profile.notVerified')}
                    </span>
                  )}
                  <Button type="button" variant="outline" onClick={() => navigate('/settings?tab=account')}>
                    {user.phoneVerified ? t('common.edit') : t('profile.verify')}
                  </Button>
                </div>
              ) : user.whatsappPhone ? (
                <div className="flex items-center gap-2">
                  <a
                    href={"https://wa.me/" + user.whatsappPhone.replace(/\D/g, '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline font-medium"
                  >
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="w-5 h-5" />
                    {user.whatsappPhone}
                  </a>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${user.phoneVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {user.phoneVerified ? t('profile.verified') : t('profile.notVerified')}
                  </span>
                  {!user.phoneVerified && (
                    <Button type="button" size="sm" variant="outline" onClick={() => navigate('/settings?tab=account')}>
                      {t('profile.verify')}
                    </Button>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.role')}:</Label>
              <span className="text-foreground capitalize">{t('profile.employer')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
              <Label className="font-semibold text-muted-foreground">{t('profile.status')}:</Label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                {user.status.toLowerCase()}
              </span>
            </div>
            {user.isVip && user.vipExpiryDate && (
              <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
                <Label className="font-semibold text-muted-foreground">{t('profile.vipMember')}:</Label>
                <span className="flex items-center text-foreground">
                  <span className="bg-yellow-500/20 text-yellow-600 text-xs font-bold px-2 py-1 rounded mr-3">VIP</span>
                  {(() => {
                    const expiryDate = parseServerDate(user.vipExpiryDate);
                    const now = new Date();
                    const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    if (daysRemaining > 0) {
                      return t('profile.validUntil', { date: fmtDate(expiryDate), days: daysRemaining });
                    } else {
                      return t('profile.expired', { date: fmtDate(expiryDate) });
                    }
                  })()}
                </span>
              </div>
            )}
            <div className="flex gap-3 pt-6 border-t border-border mt-6">
              {isEditing ? (
                <>
                  <Button onClick={handleSave} disabled={saving}>{saving ? t('profile.saving') : t('common.save')}</Button>
                  <Button variant="outline" onClick={() => {
                    setIsEditing(false);
                    setEditForm({ username: user?.username || '', lastName: user?.lastName || '', email: user?.email || '', whatsappPhone: user?.whatsappPhone || '' });
                  }}>{t('common.cancel')}</Button>
                </>
              ) : (
                <Button onClick={() => navigate('/subscription')} variant={user?.isVip && user?.vipExpiryDate ? "outline" : "default"}>
                  {user?.isVip && user?.vipExpiryDate ? t('profile.renewVip') : t('profile.upgradeVip')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderHelperProfileSkeleton = () => (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
        <div className="space-y-6">
          <Card className="border-border">
            <CardContent className="p-6 flex flex-col items-center space-y-4">
              <div className="h-24 w-24 rounded-full bg-muted" />
              <div className="w-full space-y-3">
                <div className="h-6 w-2/3 mx-auto rounded bg-muted" />
                <div className="h-4 w-1/2 mx-auto rounded bg-muted" />
                <div className="h-10 w-full rounded bg-muted" />
              </div>
            </CardContent>
          </Card>
          <div className="h-10 w-full rounded-md bg-muted" />
        </div>
        <div className="md:col-span-2 space-y-6">
          <Card className="border-border">
            <CardHeader>
              <div className="h-6 w-40 rounded bg-muted" />
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-5/6 rounded bg-muted" />
              <div className="h-4 w-2/3 rounded bg-muted" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  // HELPER without profile
  if (user?.role === 'HELPER' && profileLoading && !helperProfile) {
    return renderHelperProfileSkeleton();
  }

  if (user?.role === 'HELPER' && !helperProfile) {
    return (
      <div className="p-4 sm:p-8 max-w-3xl space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">{t('profile.title', 'My Profile')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('profile.subtitle', 'Manage your profile information')}</p>
        </div>
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle>{t('profile.basicInfo', 'Basic Information')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <MapPin className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-center max-w-md">{t('profile.noProfile')}</p>
            <Button size="lg" onClick={() => navigate('/helper/profile/new')}>{t('profile.createProfile')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // HELPER with profile
  if (user?.role === 'HELPER' && helperProfile) {
    const helper = helperProfile;
    
    const age = calculateAge(helper.dateOfBirth);

    const parseSkills = (skillsString?: string): string[] => {
      if (!skillsString) return [];
      return skillsString.split(',').map(s => s.trim()).filter(s => s.length > 0);
    };
    const skills = parseSkills(helper.skills);
    const careTypesList = parseSkills(helper.careTypes);
    const houseworkTypesList = parseSkills(helper.houseworkTypes);

    const workExperiences: WorkExperience[] = helper.workExperiences || [];
    const hasWorkExperience = workExperiences.length > 0 || Boolean(helper.workExperience && helper.workExperience.trim());


    const calculateDuration = (startDate: string, endDate?: string): string => {
      const { years: y, months: m } = calcDuration(startDate, endDate);
      if (y > 0 && m > 0) return t('profile.durationYearsMonths', { years: y, months: m });
      if (y > 0) return t('profile.durationYears', { years: y });
      if (m > 0) return t('profile.durationMonths', { months: m });
      return t('profile.lessThanOneMonth');
    };

    const hasEducation = Boolean(
      helper.educationLevel || 
      helper.educationInstitution || 
      helper.educationDegree || 
      helper.educationStartYear
    );

    const educationDocs: HelperDocument[] = (helper.documents || []).filter(d => d.documentType === 'EDUCATION_CERT');

    const getAvatarInitials = (name: string) => {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const nationalityDisplay = helper.nationality ? getDictValue(nationalityDicts, helper.nationality, helper.nationality) : t('profile.notSpecified');
    const maritalStatusDisplay = helper.maritalStatus ? getDictValue(maritalStatusDicts, helper.maritalStatus, t('profile.notSpecified')) : t('profile.notSpecified');
    const genderDisplay = helper.gender ? getDictValue(genderDicts, helper.gender, helper.gender === 'MALE' ? t('common.male') : t('common.female')) : t('profile.notSpecified');
    const religionDisplay = helper.religion ? getDictValue(religionDicts, helper.religion, helper.religion) : t('profile.notSpecified');

    return (
      <div className="grid lg:grid-cols-3 gap-6 items-start">

          {/* ── Left column: sidebar card ── */}
          <div className="lg:col-span-1">
            <div>
              <Card>
                <CardContent className="p-6 space-y-5">
                  {/* Avatar */}
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <div className="w-28 h-28 rounded-2xl overflow-hidden bg-muted ring-4 ring-background shadow-lg">
                        {helper.avatarUrl ? (
                          <img src={helper.avatarUrl} alt={helper.fullName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-primary/10">
                            <span className="text-3xl font-bold text-primary/40">{getAvatarInitials(helper.fullName)}</span>
                          </div>
                        )}
                      </div>
                      {helper.lookingForJob && (
                        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-green-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full border-2 border-background shadow-md whitespace-nowrap">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          {t('helperDetail.canWork', '可上班')}
                        </span>
                      )}
                    </div>
                    <div className="text-center">
                      <h1 className="text-xl font-bold">{helper.fullName}</h1>
                      <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 mt-1 text-sm text-muted-foreground">
                        {nationalityDisplay && <span className="flex items-center gap-1"><Globe size={14} />{nationalityDisplay}</span>}
                        {genderDisplay && <span>{genderDisplay}</span>}
                        {age != null && <span>{age} {t('helperDetail.yrsOld', '歲')}</span>}
                        {maritalStatusDisplay && <span>{maritalStatusDisplay}</span>}
                      </div>
                      <div className="flex flex-wrap justify-center gap-1 mt-2">
                        {helper.lookingForJob && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary text-secondary-foreground">{t('helperDetail.activelyLooking', '積極求職')}</span>
                        )}
                        {(helper.verifiedDocCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white">
                            <Check size={11} className="stroke-[3]" />
                            {t('helperDetail.verified', '身份已核實')} {helper.verifiedDocCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <hr className="border-border" />

                  {/* Key stats */}
                  <div className="space-y-2.5 text-sm">
                    {helper.educationLevel && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><GraduationCap size={16} />{t('helperDetail.education', '學歷')}</span>
                        <span className="font-medium">{getDictValue(educationLevelDicts, helper.educationLevel, helper.educationLevel)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2"><Briefcase size={16} />{t('helperDetail.workExperience', '工作經驗')}</span>
                      <span className="font-medium">{helper.experienceYears ?? 0} {t('helperDetail.yrs', '年')}</span>
                    </div>
                    {helper.restDayPreference && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Clock size={16} />{t('helperDetail.restDay', '休息日')}</span>
                        <span className="font-medium">{
                          helper.restDayPreference === 'ANY' ? t('jobForm.any', '不限') :
                          helper.restDayPreference === 'WEEKDAY' ? t('jobDetail.weekday', '平日') :
                          helper.restDayPreference === 'SATURDAY' ? t('jobDetail.saturday', '星期六') :
                          helper.restDayPreference === 'SUNDAY' ? t('jobDetail.sunday', '星期日') :
                          helper.restDayPreference === 'TO_BE_DISCUSS' ? t('jobForm.salaryNegotiable', '面議') :
                          helper.restDayPreference === 'FLEXIBLE' ? t('jobDetail.flexible', '彈性') :
                          helper.restDayPreference
                        }</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-2"><Activity size={16} />{t('helperDetail.expectedSalary', '期望薪金')}</span>
                      <span className="font-medium text-primary">{helper.expectedSalaryCurrency === 'HKD' ? 'HK$' : '$'}{helper.expectedSalary?.toLocaleString()}</span>
                    </div>
                    {(helper.availableStartDate || helper.lookingForJob) && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Calendar size={16} />{t('helperDetail.availableFrom', '可上班日期')}</span>
                        <span className="font-medium">
                          {helper.availableStartDate
                            ? fmtDate(helper.availableStartDate)
                            : t('common.anytime', 'ANYTIME')}
                        </span>
                      </div>
                    )}
                    {(helper.height != null || helper.weight != null) && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Activity size={16} />{t('helperDetail.physique', '身高體重')}</span>
                        <span className="font-medium">
                          {helper.height != null ? `${helper.height}${t('helperDetail.cm', 'cm')}` : ''}
                          {helper.height != null && helper.weight != null ? ' / ' : ''}
                          {helper.weight != null ? `${helper.weight}${t('helperDetail.kg', 'kg')}` : ''}
                        </span>
                      </div>
                    )}
                    {helper.numberOfChildren != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Users size={16} />{t('helperDetail.children', '子女')}</span>
                        <span className="font-medium">{t('helperDetail.childrenCount', { n: helper.numberOfChildren })}{(helper as any).childrenAges ? t('helperDetail.childrenAges', { ages: (helper as any).childrenAges.split(',').map((s: string) => s.trim()).filter(Boolean).sort((a: string, b: string) => Number(a) - Number(b)).join(', ') }) : ''}</span>
                      </div>
                    )}
                    {helper.numberOfSiblings != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Users size={16} />{t('helperDetail.siblings', '兄弟姊妹')}</span>
                        <span className="font-medium">
                          {t('helperDetail.siblingsCount', { n: helper.numberOfSiblings })}
                          {helper.siblingRank != null ? t('helperDetail.siblingRank', { n: helper.siblingRank }) : ''}
                        </span>
                      </div>
                    )}
                    {helper.spouseOccupation && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Briefcase size={16} />{t('helperDetail.spouseOccupation', '配偶職業')}</span>
                        <span className="font-medium">{helper.spouseOccupation}</span>
                      </div>
                    )}
                    {helper.religion && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Sparkles size={16} />{t('helperDetail.religion', '宗教')}</span>
                        <span className="font-medium">{getDictValue(religionDicts, helper.religion, helper.religion)}</span>
                      </div>
                    )}
                    {helper.hometownRegion && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><MapPin size={16} />{t('helperDetail.hometownRegion', '家鄉地區')}</span>
                        <span className="font-medium">{helper.hometownRegion}</span>
                      </div>
                    )}
                    {helper.accommodation && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Home size={16} />{t('helperDetail.accommodationNeed', '住宿要求')}</span>
                        <span className="font-medium">{getAccommodationOptions(t).find(o => o.value === helper.accommodation)?.label || helper.accommodation}</span>
                      </div>
                    )}
                    {helper.currentLocation && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><MapPin size={16} />{t('helperDetail.currentLocation', '現居地')}</span>
                        <span className="font-medium">{getWorkLocation(helper.currentLocation || '')}</span>
                      </div>
                    )}
                  </div>

                  <hr className="border-border" />

                  <Button variant="outline" className="w-full gap-2" onClick={() => navigate(`/helper/profile/${helper.id}/edit`)}>
                    <Edit size={16} />
                    {t('helperDetail.editProfile', '編輯個人資料')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>{/* end left col */}

          {/* ── Right column: about + education + work experience + skills ── */}
          <div className="lg:col-span-2 space-y-4">
            {/* Section 2 */}
            <div className="space-y-2">
            <Card className="rounded-xl border-border bg-card text-card-foreground shadow-sm">
              <CardHeader className="border-b border-border pb-2 p-3">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <Sparkles className="w-3.5 h-3.5" />
                  </span>
                  {t('profile.about')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xs text-foreground/90 leading-relaxed bg-muted/20 p-3 rounded-lg border border-border/50 mb-3">
                  {helper.selfIntroduction ? (
                    <p className="whitespace-pre-wrap">{helper.selfIntroduction}</p>
                  ) : (
                    <p>{t('profile.defaultIntro', { age, nationality: nationalityDisplay, experience: helper.workExperience || t('profile.defaultExperience') })}</p>
                  )}
                </div>

                {(careTypesList.length > 0 || houseworkTypesList.length > 0) && (
                  <div className="space-y-2.5">
                    {careTypesList.length > 0 && (
                      <div>
                        <h3 className="text-xs font-bold text-muted-foreground mb-1.5">{t('profile.careTypes')}</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {careTypesList.map(type => (
                            <span key={type} className="px-2 py-0.5 bg-primary/5 text-primary border border-primary/20 rounded text-xs font-medium">
                              {getDictValue(careDicts, type, type)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {houseworkTypesList.length > 0 && (
                      <div>
                        <h3 className="text-xs font-bold text-muted-foreground mb-1.5">{t('profile.houseworkTypes')}</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {houseworkTypesList.map(type => (
                            <span key={type} className="px-2 py-0.5 bg-primary/5 text-primary border border-primary/20 rounded text-xs font-medium">
                              {getDictValue(houseworkDicts, type, type)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-xl border-border bg-card text-card-foreground shadow-sm">
              <CardHeader className="border-b border-border pb-2 p-3">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <GraduationCap className="w-3.5 h-3.5" />
                  </span>
                  {t('profile.education')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                {hasEducation ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <div className="text-muted-foreground">{t('profile.educationLevel', 'Education Level')}</div>
                      <div className="font-semibold mt-0.5">{helper.educationLevel ? getDictValue(educationLevelDicts, helper.educationLevel, helper.educationLevel) : '-'}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{t('profile.educationInstitution', 'Institution')}</div>
                      <div className="font-semibold mt-0.5">{helper.educationInstitution || '-'}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{t('profile.educationDegree', 'Degree')}</div>
                      <div className="font-semibold mt-0.5">{helper.educationDegree || '-'}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">{t('profile.duration', 'Duration')}</div>
                      <div className="font-semibold mt-0.5">
                        {helper.educationStartYear ? `${helper.educationStartYear}/${String(helper.educationStartMonth || 1).padStart(2, '0')} - ${helper.educationOngoing ? t('profile.present') : helper.educationEndYear ? `${helper.educationEndYear}/${String(helper.educationEndMonth || 1).padStart(2, '0')}` : ''}` : '-'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-3 text-muted-foreground text-xs">
                    {t('common.noData')}
                  </div>
                )}
                {educationDocs.length > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-border/50">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block"></span>
                      {t('profile.educationDocuments')}
                    </p>
                    <div className="space-y-1.5">
                      {educationDocs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between gap-2 p-2 border border-border rounded bg-background">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={12} className="text-muted-foreground shrink-0" />
                            <span className="text-xs font-medium text-foreground/90 truncate" title={doc.fileName}>{doc.fileName}</span>
                          </div>
                          <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                             className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/15 border border-primary/20 px-2 py-0.5 rounded transition-colors">
                            <Download size={11} />
                            {t('profile.documents.download')}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

            {/* Section 3 */}
            <div className="space-y-2">
            <Card className="rounded-xl border-border bg-card text-card-foreground shadow-sm">
                  <CardHeader className="border-b border-border pb-2 p-3">
                    <CardTitle className="text-sm font-bold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                        <Briefcase className="w-3.5 h-3.5" />
                      </span>
                      {t('profile.workingExperience')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 space-y-2.5">
                    {hasWorkExperience ? (
                      workExperiences.length > 0 ? (
                        workExperiences.map((exp, index) => (
                          <div key={exp.id || index} className="border border-border/50 rounded-lg p-2.5 shadow-sm hover:border-primary/30 transition-all">
                            <div className="flex justify-between items-start mb-1.5">
                              <h4 className="text-xs font-bold text-foreground">
                                {exp.employerFamily ? `${exp.employerFamily} · ` : ''}{exp.title}
                              </h4>
                              <div className="text-[10px] text-muted-foreground font-medium whitespace-nowrap ml-2">
                                {fmtDate(exp.startDate)} - {exp.isCurrent || !exp.endDate ? t('profile.present') : fmtDate(exp.endDate)}
                                <span className="ml-1 text-primary font-bold">({calculateDuration(exp.startDate, exp.endDate)})</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-1.5 text-[10px] text-muted-foreground mb-1.5">
                              {exp.location && <span className="bg-muted px-1.5 py-0.5 rounded">{getWorkLocation(exp.location)}</span>}
                              {exp.familySize && (
                                <span className="bg-muted px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <Users size={10} className="shrink-0" />
                                  {(() => {
                                    const adults = exp.familySize.adults || 0;
                                    const children = exp.familySize.children || 0;
                                    const babies = exp.familySize.babies || 0;
                                    const parts = [];
                                    if (adults > 0) parts.push(`${t('profile.adults')} ${adults}`);
                                    if (children > 0) parts.push(`${t('profile.children')} ${children}`);
                                    if (babies > 0) parts.push(`${t('profile.babies')} ${babies}`);
                                    return parts.length > 0 ? parts.join(' · ') : '';
                                  })()}
                                </span>
                              )}
                              {exp.isCurrent && <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{t('profile.working')}</span>}
                            </div>
                            
                            {(exp.skills && exp.skills.length > 0) && (
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {exp.skills.map((skill, i) => (
                                  <span key={i} className="px-1.5 py-0.5 bg-primary/5 text-primary text-[10px] font-semibold rounded border border-primary/20">
                                    {getSkillDisplayValue(skill)}
                                  </span>
                                ))}
                              </div>
                            )}

                            {exp.notes && exp.notes.length > 0 && (
                              <div className="mb-1.5 flex flex-wrap gap-1">
                                {exp.notes.map((note, i) => (
                                  <span key={i} className="px-1.5 py-0.5 bg-muted text-foreground/80 text-[10px] font-medium rounded border border-border">
                                    {note}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {exp.specialNeedsNote && (
                              <p className="mb-1.5 text-[10px] text-muted-foreground bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                <span className="font-semibold text-amber-800">{t('profile.specialNeedsNote')}: </span>
                                {exp.specialNeedsNote}
                              </p>
                            )}
                            
                            {exp.hasRecommendationLetter && (
                              <div className="mb-1.5">
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500/10 text-green-700 border border-green-500/20 text-[10px] font-semibold rounded">
                                  <Check size={10} className="stroke-[3]" />
                                  {t('profile.workExperience.hasRecommendationLetter')}
                                </span>
                              </div>
                            )}

                            {exp.documents && exp.documents.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-border/50">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block"></span>
                                  {t('profile.workExperience.documents')}
                                </p>
                                <div className="space-y-1.5">
                                  {exp.documents.map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between gap-2 p-2 border border-border rounded bg-background">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <FileText size={12} className="text-muted-foreground shrink-0" />
                                        <span className="text-xs font-medium text-foreground/90 truncate" title={doc.fileName}>{doc.fileName}</span>
                                      </div>
                                      <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                                         className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/15 border border-primary/20 px-2 py-0.5 rounded transition-colors">
                                        <Download size={11} />
                                        {t('profile.documents.download')}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            </div>
                        ))
                      ) : (
                        <div className="bg-muted/20 border border-border/50 rounded-lg p-3 shadow-sm">
                          <p className="text-foreground/90 whitespace-pre-wrap leading-relaxed text-xs">{helper.workExperience}</p>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Briefcase className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-xs">{t('common.noData')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
            <Card className="rounded-xl border-border bg-card text-card-foreground shadow-sm">
                  <CardHeader className="border-b border-border pb-2 p-3">
                    <CardTitle className="text-sm font-bold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                        <ChefHat className="w-3.5 h-3.5" />
                      </span>
                      {t('common.skills')}
                    </CardTitle>
                  </CardHeader>
                   <CardContent className="p-3 space-y-4">
                     {/* Cooking Skills */}
                     <div>
                       <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                         <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                         {t('common.cookingSkills')}
                         <CookingProficiencyTooltip />
                       </h3>
                       {helper.cookingSkillsText ? (
                         <div className="flex flex-wrap gap-1.5">
                           {helper.cookingSkillsText.split(',').filter(Boolean).map((entry, index) => {
                             const [skillCode, levelCode] = entry.split(':');
                             const skillLabel = getDictValue(cookingSkillsDicts, skillCode, skillCode);
                             const levelLabel = levelCode ? getCookingLevelLabel(levelCode) : null;
                             return (
                               <span key={index} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background border border-border rounded-lg text-xs font-medium hover:border-primary/30 transition-colors">
                                 <Check size={11} className="text-primary stroke-[3]" />
                                 {skillLabel}{levelLabel ? ` (${levelLabel})` : ''}
                               </span>
                             );
                           })}
                         </div>
                       ) : helper.cookingSkills && helper.cookingSkills.length > 0 ? (
                         <div className="flex flex-wrap gap-1.5">
                           {helper.cookingSkills.map((skill, index) => {
                             const skillLabel = getDictValue(cookingSkillsDicts, skill, skill);
                             return (
                               <span key={index} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background border border-border rounded-lg text-xs font-medium hover:border-primary/30 transition-colors">
                                 <Check size={11} className="text-primary stroke-[3]" />
                                 {skillLabel}
                               </span>
                             );
                           })}
                         </div>
                       ) : (
                         <p className="text-muted-foreground italic text-xs">{t('common.noData')}</p>
                       )}
                     </div>

                     {/* Other Skills */}
                     {skills.length > 0 && (
                       <div>
                         <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                           <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                           {t('profile.otherSkillsSection')}
                         </h3>
                         <div className="flex flex-wrap gap-1.5">
                           {skills.map((skill, index) => {
                             const skillLabel = getDictValue(otherSkillsDicts, skill, skill);
                             return (
                               <span key={`other-${index}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background border border-border rounded-lg text-xs font-medium hover:border-primary/30 transition-colors">
                                 <Check size={11} className="text-primary stroke-[3]" />
                                 {skillLabel}
                               </span>
                             );
                           })}
                         </div>
                       </div>
                     )}
                   </CardContent>
                 </Card>
           </div>
          </div>{/* end right col */}
        </div>
    );
  }

  return null;
};

export default ProfilePage;
